import { endOfDay, format, isValid, parse, startOfDay } from "date-fns"
import { find, includes, isArray } from "lodash"
import { ISO_DATE_REGEX } from "../constants"
import { countries } from "../constants/countries"
import { TimeZoneType } from "../constants/timezones"
import { getDefaultDateFormat } from "../utils"
import { invariant } from "../utils/invariant"

export class DateTimeService {
  private dateFormat: string
  private timeFormat: string
  private enableTimeComponent: boolean
  private timeZone: TimeZoneType
  static MAX_PROJECT_YEARS = 15
  static MAX_PROJECT_DURATION_MS = DateTimeService.MAX_PROJECT_YEARS * 365.25 * 24 * 60 * 60 * 1000

  private getCountryFromTimezone() {
    const timezone = this.getTimeZone()
    return find(countries, ({ timezones }) => {
      return isArray(timezones) && includes(timezones, timezone)
    })
  }

  static isValidDate(value: Date | string): boolean {
    if (value instanceof Date) return !isNaN(value as any)
    if (typeof value === "string") return ISO_DATE_REGEX.test(value)
    return false
  }

  static getOsTimeZone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone as TimeZoneType
  }

  constructor({ enableTimeComponent = false, ...options }: DateTimeServiceOptions = {}) {
    this.enableTimeComponent = enableTimeComponent
    this.timeZone = options.timeZone || (this.getOsTimeZone() as TimeZoneType)
    this.dateFormat = options.dateFormat || this.getDateFormat()
    this.timeFormat = options.timeFormat || this.getTimeFormat()
  }

  format(date: Date | string, formatStr = this.getFormat()) {
    if (DateTimeService.isValidDate(date)) {
      return format(new Date(date), formatStr)
    } else {
      throw new Error("Date is invalid")
    }
  }

  formatWithoutYear(date: Date | string, formatStr = this.getFormat()) {
    if (DateTimeService.isValidDate(date)) {
      let formattedDate = format(new Date(date), formatStr).replace(new Date(date).getFullYear().toString(), "")
      const lastChar = formattedDate.charAt(formattedDate.length - 1)
      const firstChar = formattedDate.charAt(0)
      if (!Number(firstChar) && firstChar !== "0") {
        formattedDate = formattedDate.substring(1)
      }
      if (!Number(lastChar)) {
        formattedDate = formattedDate.substring(0, formattedDate.length - 1)
      }
      return formattedDate
    } else {
      throw new Error("Date is invalid")
    }
  }

  convertToTimeZoneAndFormat(
    date: Date | string,
    formatStr = this.getFormat(),
    timeZone: TimeZoneType = this.timeZone
  ) {
    invariant(DateTimeService.isValidDate(date), "Date is invalid")
    const timezonedDate = new Date(new Date(date).toLocaleString("en-US", { timeZone }))
    return format(timezonedDate, formatStr)
  }

  getOsTimeZone() {
    return DateTimeService.getOsTimeZone()
  }

  getTimeZone() {
    return this.timeZone
  }

  getDateFormat() {
    const country = this.getCountryFromTimezone()
    return this.dateFormat || country?.preferredDateFormat || getDefaultDateFormat()
  }

  getTimeFormat() {
    return this.timeFormat || "HH:mm"
  }

  getFormat() {
    if (this.enableTimeComponent) return `${this.dateFormat} ${this.timeFormat}`
    else return `${this.dateFormat}`
  }

  getDate(dateString: string, formatString = this.getFormat(), date = new Date()): Date | null {
    const parsed = parse(dateString, formatString, date)
    const isValidDate = isValid(parsed)
    return isValidDate ? parsed : null
  }

  getEnableTimeComponent(): boolean {
    return this.enableTimeComponent
  }

  setEnableTimeComponent(enableTimeComponent: boolean) {
    this.enableTimeComponent = enableTimeComponent
  }

  getMinutes(date: Date | string, timeZone: TimeZoneType = this.getTimeZone()): number {
    invariant(DateTimeService.isValidDate(date), "Date is invalid")
    invariant(timeZone, "Timezone is required")
    return new Date(new Date(date).toLocaleString("en-US", { timeZone })).getMinutes()
  }

  getHours(date: Date | string, timeZone: TimeZoneType = this.getTimeZone()): number {
    invariant(DateTimeService.isValidDate(date), "Date is invalid")
    invariant(timeZone, "Timezone is required")
    return new Date(new Date(date).toLocaleString("en-US", { timeZone })).getHours()
  }

  getDoubleDigitTime(date: Date | string, timeZone: TimeZoneType = this.getTimeZone()): string {
    const hours = this.getHours(date, timeZone)
    const minutes = this.getMinutes(date, timeZone)
    const doubleDigitHours = hours < 10 ? `0${hours}` : hours
    const doubleDigitMinutes = minutes < 10 ? `0${minutes}` : minutes
    return `${doubleDigitHours}:${doubleDigitMinutes}`
  }

  isMidnight(date: Date | string, timeZone: TimeZoneType = this.getTimeZone()) {
    invariant(DateTimeService.isValidDate(date), "Date is invalid")
    const dateInstance = new Date(date)
    const hasMidnightHours = this.getHours(dateInstance, timeZone) === 0
    const hasMidnightMinutes = this.getMinutes(dateInstance, timeZone) === 0
    const hasMidnightSeconds = dateInstance.getSeconds() === 0
    const hasMidnightMilliseconds = dateInstance.getMilliseconds() === 0
    return hasMidnightHours && hasMidnightMinutes && hasMidnightSeconds && hasMidnightMilliseconds
  }

  hasMidnightTime(date: Date | string) {
    if (DateTimeService.isValidDate(date)) {
      const validDate = new Date(date)
      const time = validDate.getMinutes()
      const hour = validDate.getHours()
      return time === 0 && hour === 0
    } else {
      throw new Error("Date is invalid")
    }
  }

  isPastDate(date: Date) {
    return new Date(date) < this.startOfDay(new Date())
  }

  selectedTzAndOsTzAreDifferent() {
    return this.timeZone !== this.getOsTimeZone()
  }

  removeTimezoneOffset(date: Date | string, baseTimeZone: TimeZoneType = this.getOsTimeZone()): Date {
    if (baseTimeZone === this.timeZone) return new Date(date)
    if (DateTimeService.isValidDate(date)) {
      const offset = this.calculateTimezoneOffSet(new Date(date), baseTimeZone, this.timeZone)
      const newDAte = new Date(date).getTime() - offset * 60 * 1000
      return new Date(newDAte)
    } else {
      throw new Error("Date is invalid")
    }
  }

  addTimezoneOffset(date: Date | string, baseTimeZone: TimeZoneType = this.getOsTimeZone()): Date {
    if (baseTimeZone === this.timeZone) return new Date(date)
    if (DateTimeService.isValidDate(date)) {
      const offset = this.calculateTimezoneOffSet(new Date(date), baseTimeZone, this.timeZone)
      const newDAte = new Date(date).getTime() + offset * 60 * 1000
      return new Date(newDAte)
    } else {
      throw new Error("Date is invalid")
    }
  }

  startOfDay(date: Date): Date {
    const timeZoneOffSet = this.calculateTimezoneOffSet(date, this.getOsTimeZone(), this.timeZone)
    const dateInDestinationTz = date.getTime() + timeZoneOffSet * 60 * 1000
    const startOfDayDate = new Date(startOfDay(dateInDestinationTz).getTime() - timeZoneOffSet * 60 * 1000)
    return startOfDayDate
  }

  endOfDay(date: Date): Date {
    const timeZoneOffSet = this.calculateTimezoneOffSet(date, this.getOsTimeZone(), this.timeZone)
    const dateInDestinationTz = date.getTime() + timeZoneOffSet * 60 * 1000
    const endOfDayDate = new Date(endOfDay(dateInDestinationTz).getTime() - timeZoneOffSet * 60 * 1000)
    return endOfDayDate
  }

  calculateTimezoneOffSet(date: Date, baseTimeZone: TimeZoneType, differentTimeZone: TimeZoneType): number {
    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }))
    const baseTimeZoneDate = new Date(date.toLocaleString("en-US", { timeZone: baseTimeZone }))
    const differentTimeZoneDate = new Date(date.toLocaleString("en-US", { timeZone: differentTimeZone }))
    const baseTzOffSet = (utcDate.getTime() - baseTimeZoneDate.getTime()) / 6e4
    const differentTzOffSet = (utcDate.getTime() - differentTimeZoneDate.getTime()) / 6e4
    return differentTzOffSet - baseTzOffSet
  }

  getLargestPossibleDate() {
    return new Date(8.64e15)
  }

  static isExpiredTodayOrBefore(date: Date | string | null): boolean {
    if (!date) return false
    if (!this.isValidDate(date)) return false

    return new Date(date).getTime() <= Date.now()
  }

  isAllowedDays(
    date: Date,
    options: {
      disabledDays?: DayOfWeek[] // 0–6 (Sun–Sat)
      disabledDates?: (Date | string)[] // exact dates (holidays)
    },
    timeZone: TimeZoneType = this.timeZone
  ): boolean {
    invariant(options, "options is required")

    const { disabledDays = [], disabledDates = [] } = options

    invariant(isArray(disabledDays), "disabledDays must be an array")
    invariant(isArray(disabledDates), "disabledDates must be an array")

    // Normalize date into selected timezone
    const zonedDate = new Date(date.toLocaleString("en-US", { timeZone }))
    zonedDate.setHours(0, 0, 0, 0)

    /* ---------- day-of-week check ---------- */
    const dayOfWeek = zonedDate.getDay() as DayOfWeek // 0–6
    if (disabledDays.includes(dayOfWeek)) return false

    /* ---------- exact date check ---------- */
    const yyyyMmDd = zonedDate.toISOString().slice(0, 10)

    const isHoliday = disabledDates.some((d) => {
      const holiday = d instanceof Date ? new Date(d.toLocaleString("en-US", { timeZone })) : new Date(d)

      holiday.setHours(0, 0, 0, 0)
      return holiday.toISOString().slice(0, 10) === yyyyMmDd
    })

    if (isHoliday) return false

    return true
  }
  static validateProjectDuration(start?: Date | string, end?: Date | string): boolean {
    // Allow setting only one boundary; validate only when both are present.
    // This avoids blocking users from entering planned start/end independently.
    if (!start || !end) return true

    invariant(DateTimeService.isValidDate(start), "Start date is invalid")
    invariant(DateTimeService.isValidDate(end), "End date is invalid")

    const startDate = new Date(start)
    const endDate = new Date(end)
    const diff = endDate.getTime() - startDate.getTime()
    if (diff > DateTimeService.MAX_PROJECT_DURATION_MS) {
      return false
    }
    return true
  }
  static getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    const dayNum = d.getUTCDay() || 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  }
  static toLocalDateOnly = (d: string) => {
    const date = new Date(d)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }
  // Converts date to ISO Format without converting the time itself to local timezone.
  // This is required in cases where we want to preserve the date as is without any timezone conversion.
  static toLocalISOStringFormat = (date: Date) => {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    const hours = String(d.getHours()).padStart(2, "0")
    const minutes = String(d.getMinutes()).padStart(2, "0")
    const seconds = String(d.getSeconds()).padStart(2, "0")
    const ms = String(d.getMilliseconds()).padStart(3, "0")
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}Z`
  }
}

export type DateTimeServiceOptions = {
  dateFormat?: string
  timeFormat?: string
  enableTimeComponent?: boolean
  timeZone?: TimeZoneType
}
